// App.tsx
import React, { useEffect, useState } from 'react';
import { useRoutes, RouteObject, useLocation } from "react-router-dom";
import Login from "./pages/Login/login";
import Register from "./pages/Login/register";
import Dashboard from "./pages/Dashboard/dashboard";
import MapPage from "./pages/Map/map";
import ScheduleTable from "./pages/ScheduleTable/new/scheduleTable_new";
import NewDatabase from "./pages/New Database/new_database";
import Approvals from "./pages/Approvals/approvals";
import PdfApprovals from "./pages/PDF Approvals/pdfApprovals";
import OpsTables from './pages/OpsTables/opsTables';
import AccTables from './pages/AccTables/accTables';
import AdminPage from './pages/Admin/admin';
import AuditTable from './pages/Investigations/InvestigationsContainer';
import TriggersPage from './pages/Triggers/triggers';
import ChangelogPage from './pages/Changelog/changelog';
import MainMTDPage from './pages/MTD/mainMTDPage';
import MTDTestPage from './pages/MTD/mtdTest';
import KpiPage from './pages/Kpi/kpi';
import Sandbox from './pages/Sandbox/Sandbox';
import Layout from "./components/layout/Layout";
import { AuthProvider } from "./contexts/authContext";
import { UserInfoProvider } from "./contexts/apiService";
import { DataManagerProvider } from "./contexts/dataManager";
import { UserAccessProvider } from './contexts/userAccess';
import { TicketsProvider } from './contexts/pdfTicketsContext';
import { MonthNavigationProvider } from './contexts/monthNavigationContext';
import { getAuth, onAuthStateChanged, User } from "firebase/auth";
import { DarkModeProvider } from '../src/contexts/darkModeContext';
import { DarkModeToggle } from '../src/components/common/DarkModeToggle';
import { useApiData } from './contexts/apiService';
import { trackEvent } from './contexts/userAnalytics';
import '../src/components/css_files/darkMode.css';
import './App.css';

const App: React.FC = () => {
	const { pathname } = useLocation();
	const [user, setUser] = useState<User | null>(null);
	const [loading, setLoading] = useState(true);
	const [bannerDismissed, setBannerDismissed] = useState(false);

	useEffect(() => {
		const auth = getAuth();
		const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
			setUser(currentUser);
			setLoading(false);
		});

		return () => unsubscribe();
	}, []);

	useEffect(() => {
		if (loading) return;

		trackEvent("page_view", {
			path: pathname,
		});
	}, [pathname, loading]);

	const routesArray: RouteObject[] = [
		{
			element: <Layout />,
			children: [
				{
					path: "/home",
					element: <Dashboard />,
				},
				{
					path: "/maps",
					element: <MapPage />,
				},
				{
					path: "/schedule",
					element: <ScheduleTable />,
				},
				{
					path: "/ticketdata",
					element: <NewDatabase />,
				},
				{
					path: "/mtdsummary",
					element: <MainMTDPage />,
				},
				{
					path: "/mtdtest",
					element: <MTDTestPage />,
				},
				{
					path: "/approvals",
					element: <Approvals />,
				},
				{
					path: "/pdfapprovals",
					element: <PdfApprovals />,
				},
				{
					path: "/opstables",
					element: <OpsTables />,
				},
				{
					path: "/acctables",
					element: <AccTables />,
				},
				{
					path: "/triggers",
					element: <TriggersPage />,
				},
				{
					path: "/changelog",
					element: <ChangelogPage />,
				},
				{
					path: "/investigations",
					element: <AuditTable />,
				},
				{
					path: "/admin",
					element: <AdminPage />,
				},
				{
					path: "/kpi",
					element: <KpiPage/>,
				},
				{
					path: "/sandbox",
					element: <Sandbox/>,
				},
			],
		},
		{
			path: "/login",
			element: <Login />,
		},
		{
			path: "/register",
			element: <Register />,
		},
		{
			path: "*",
			element: <Login />,
		},
	];

  	let routesElement = useRoutes(routesArray);
	const showHeader = (
		window.location.hostname !== 'internal.rockbridgepetroleum.com' &&
		window.location.hostname !== 'rb-internal-frontend-778778295326.us-central1.run.app'
	);

	if (loading) {
		return <div>Loading...</div>;
	}

	// Inner component to access context
	const AppContent = () => {
		const { clientIP, clientInfo, dbInfo } = useApiData();

		return (
			<div>
				{showHeader && !bannerDismissed && (
					<div className='devMode' style={{ position: 'relative', paddingRight: '40px' }}>
						Development/Test Environment
						{dbInfo && (
							<span style={{ 
								marginLeft: '10px', 
								opacity: 0.9, 
								fontSize: '0.9em',
								fontWeight: 'bold',
								background: dbInfo.is_production ? 'rgba(255, 0, 0, 0.3)' : 'rgba(0, 255, 0, 0.2)',
								padding: '2px 8px',
								borderRadius: '4px'
							}}>
								| DB: {dbInfo.instance_name}
							</span>
						)}
						{clientIP && (
							<span style={{ marginLeft: '10px', opacity: 0.8, fontSize: '0.9em' }}>
								| IP: {clientIP}
							</span>
						)}
						{clientInfo && (
							<>
								<span style={{ marginLeft: '10px', opacity: 0.8, fontSize: '0.9em' }}>
									| {clientInfo.browser} on {clientInfo.os}
								</span>
								{clientInfo.fingerprint && (
									<span style={{ marginLeft: '10px', opacity: 0.8, fontSize: '0.9em' }}>
										| Device: {clientInfo.fingerprint}
									</span>
								)}
							</>
						)}
						<button
							style={{
								position: 'absolute',
								right: '10px',
								top: '50%',
								transform: 'translateY(-50%)',
								background: 'transparent',
								border: 'none',
								fontSize: '1.5em',
								cursor: 'pointer',
								color: '#fff',
								opacity: 0.7,
								lineHeight: '1',
								padding: '0 5px'
							}}
							aria-label="Dismiss banner"
							onClick={() => setBannerDismissed(true)}
							onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
							onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
						>
							×
						</button>
					</div>
				)}
				{showHeader && bannerDismissed && (
					<div style={{
						position: 'fixed',
						top: '5px',
						right: '10px',
						zIndex: 9999,
						background: 'rgba(255, 163, 24, 0.9)',
						color: '#fff',
						padding: '4px 12px',
						fontSize: '0.85em',
						borderRadius: '6px',
						boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
						pointerEvents: 'none'
					}}>
						Test Mode
					</div>
				)}
				{routesElement}
			</div>
		);
	};
  
 	return (
		<UserInfoProvider user={user}>
			<DataManagerProvider>
				<MonthNavigationProvider>
					<UserAccessProvider>
						<TicketsProvider user={user}>
							<AuthProvider>
								<DarkModeProvider>
									<AppContent />
								</DarkModeProvider>
							</AuthProvider>
						</TicketsProvider>
					</UserAccessProvider>
				</MonthNavigationProvider>
			</DataManagerProvider>
		</UserInfoProvider>
  	);
}

export default App;
